import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
    Button,
    Card,
    DatePicker,
    Form,
    Input,
    Pagination,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
} from 'antd';
import {useAppStore} from '@/page/app/context';
import {useAuthStore} from '@/context/auth';
import {getPushBatchLogs, getPushUserLogs} from '@/api/hrPush';
import HistoryDetailModal from '../components/HistoryDetailModal';
import {getAuthorizedSpecificTypes, ROUTINE_TYPES, SPECIFIC_TYPES} from '../routineUtils';
import {formatApiTime} from '../utils';
import AppContent from '@/page/component/AppLayout/AppContent';

const CATEGORY_LABELS = {
    routine: '常规消息推送',
    specific: '特定消息推送',
};

const ALL_TYPES = {...ROUTINE_TYPES, ...SPECIFIC_TYPES};

// 旧的服务周年推送类型已拆分/停用，历史日志里的旧值仅做展示兼容
const LEGACY_TYPE_LABELS = {
    SERVICE_ANNIVERSARY: '服务周年推送（旧）',
};

const taskTypeLabel = value => {
    if (!value) return value;
    const key = String(value).toUpperCase();
    return ALL_TYPES[key] || LEGACY_TYPE_LABELS[key] || value;
};

function statusTag(status) {
    if (status === 2) return <Tag color="green">● 成功</Tag>;
    if (status === 3) return <Tag color="orange">● 部分失败</Tag>;
    if (status === 4) return <Tag color="red">● 失败</Tag>;
    return <Tag>{status ?? '-'}</Tag>;
}

function pushStatusTag(status) {
    if (status === 1) return <Tag color="green">● 成功</Tag>;
    if (status === 2) return <Tag color="red">● 失败</Tag>;
    return <Tag>未发送</Tag>;
}

function buildParams(form) {
    const {range, ...rest} = form;
    return {
        ...rest,
        start: range?.[0]?.startOf('day').toISOString(),
        end: range?.[1]?.endOf('day').toISOString(),
    };
}

function BatchLogs({appId, categoryOptions, taskTypeOptions}) {
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});
    const [detailRow, setDetailRow] = useState(null);

    const load = useCallback(
        async (current = 1, size = pagination.pageSize) => {
            setLoading(true);
            try {
                const {data} = await getPushBatchLogs(appId, {
                    ...buildParams(form.getFieldsValue()),
                    current,
                    size,
                });
                setRows(data?.records || []);
                setPagination(old => ({...old, current, pageSize: size, total: data?.total || 0}));
            } finally {
                setLoading(false);
            }
        },
        [appId, form, pagination.pageSize]
    );

    useEffect(() => {
        load(1);
    }, [load]);

    const columns = [
        {
            title: '推送类别',
            dataIndex: 'pushCategory',
            width: 130,
            render: value => CATEGORY_LABELS[value] || value,
        },
        {
            title: '任务类型',
            dataIndex: 'taskType',
            width: 140,
            render: taskTypeLabel,
        },
        {title: '任务名称', dataIndex: 'taskName'},
        {title: '消息模板', dataIndex: 'templateName', width: 180},
        {title: '发送人数', dataIndex: 'targetCount', width: 90},
        {title: '成功', dataIndex: 'successCount', width: 80},
        {title: '失败', dataIndex: 'failCount', width: 80},
        {title: '执行状态', dataIndex: 'status', width: 110, render: statusTag},
        {title: '开始时间', dataIndex: 'executeTime', width: 170, render: formatApiTime},
        {
            title: '耗时',
            dataIndex: 'durationMs',
            width: 90,
            render: value => (value ? `${Math.floor(value / 1000)}s` : '-'),
        },
        {
            title: '操作',
            width: 80,
            fixed: 'right',
            render: (_, row) => (
                <Button type="link" onClick={() => setDetailRow(row)}>
                    详情
                </Button>
            ),
        },
    ];

    return (
        <>
            <Form form={form} layout="inline" onFinish={() => load(1)} style={{marginBottom: 16}}>
                <Form.Item name="pushCategory">
                    <Select
                        placeholder="推送类别"
                        allowClear
                        style={{width: 150}}
                        options={categoryOptions}
                    />
                </Form.Item>
                <Form.Item name="taskType">
                    <Select
                        placeholder="任务类型"
                        allowClear
                        style={{width: 150}}
                        options={taskTypeOptions}
                    />
                </Form.Item>
                <Form.Item name="status">
                    <Select
                        placeholder="执行状态"
                        allowClear
                        style={{width: 120}}
                        options={[
                            {value: 2, label: '成功'},
                            {value: 3, label: '部分失败'},
                            {value: 4, label: '失败'},
                        ]}
                    />
                </Form.Item>
                <Form.Item name="range">
                    <DatePicker.RangePicker showTime />
                </Form.Item>
                <Form.Item name="keyword">
                    <Input placeholder="任务名称/创建人" allowClear />
                </Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit">
                        查询
                    </Button>
                    <Button
                        onClick={() => {
                            form.resetFields();
                            load(1);
                        }}
                    >
                        重置
                    </Button>
                </Space>
            </Form>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                loading={loading}
                scroll={{x: 1350, y: 'calc(100vh - 430px)'}}
                pagination={false}
            />
            <Pagination
                style={{marginTop: 16, textAlign: 'right'}}
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showTotal={total => `共 ${total} 条记录`}
                onChange={(current, size) => load(current, size)}
            />
            {detailRow && (
                <HistoryDetailModal
                    appId={appId}
                    historyId={detailRow.id}
                    taskName={detailRow.taskName || detailRow.taskId}
                    onClose={() => setDetailRow(null)}
                />
            )}
        </>
    );
}

BatchLogs.propTypes = {
    appId: PropTypes.string.isRequired,
    categoryOptions: PropTypes.array.isRequired,
    taskTypeOptions: PropTypes.array.isRequired,
};

function UserLogs({appId, categoryOptions, taskTypeOptions}) {
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});
    const [detailRow, setDetailRow] = useState(null);

    const load = useCallback(
        async (current = 1, size = pagination.pageSize) => {
            setLoading(true);
            try {
                const {data} = await getPushUserLogs(appId, {
                    ...buildParams(form.getFieldsValue()),
                    current,
                    size,
                });
                setRows(data?.records || []);
                setPagination(old => ({...old, current, pageSize: size, total: data?.total || 0}));
            } finally {
                setLoading(false);
            }
        },
        [appId, form, pagination.pageSize]
    );

    useEffect(() => {
        load(1);
    }, [load]);

    const columns = [
        {title: '姓名', dataIndex: 'userName', width: 120},
        {title: '员工号', dataIndex: 'employeeNumber', width: 120},
        {title: '角色/职位', dataIndex: 'position', width: 140, render: value => value || '-'},
        {
            title: '积分截止日',
            dataIndex: 'pointsExpireDate',
            width: 130,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
        },
        {
            title: '剩余积分',
            dataIndex: 'remainingPoints',
            width: 100,
            render: value => (value == null ? '-' : value),
        },
        {
            title: '推送类别',
            dataIndex: 'pushCategory',
            width: 130,
            render: value => CATEGORY_LABELS[value] || value,
        },
        {
            title: '任务类型',
            dataIndex: 'taskType',
            width: 140,
            render: taskTypeLabel,
        },
        {title: '任务名称', dataIndex: 'taskName'},
        {title: '消息模板', dataIndex: 'templateName', width: 180},
        {title: '批次时间', dataIndex: 'executeTime', width: 170, render: formatApiTime},
        {title: '发送状态', dataIndex: 'pushStatus', width: 100, render: pushStatusTag},
        {title: '错误信息', dataIndex: 'errorMessage', render: value => value || '-'},
        {
            title: '操作',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <Button type="link" onClick={() => setDetailRow(row)}>
                    批次详情
                </Button>
            ),
        },
    ];

    return (
        <>
            <Form form={form} layout="inline" onFinish={() => load(1)} style={{marginBottom: 16}}>
                <Form.Item name="keyword">
                    <Input placeholder="姓名/员工号/任务名称" allowClear />
                </Form.Item>
                <Form.Item name="pushCategory">
                    <Select
                        placeholder="推送类别"
                        allowClear
                        style={{width: 150}}
                        options={categoryOptions}
                    />
                </Form.Item>
                <Form.Item name="taskType">
                    <Select
                        placeholder="任务类型"
                        allowClear
                        style={{width: 150}}
                        options={taskTypeOptions}
                    />
                </Form.Item>
                <Form.Item name="pushStatus">
                    <Select
                        placeholder="发送状态"
                        allowClear
                        style={{width: 120}}
                        options={[
                            {value: 1, label: '成功'},
                            {value: 2, label: '失败'},
                        ]}
                    />
                </Form.Item>
                <Form.Item name="range">
                    <DatePicker.RangePicker showTime />
                </Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit">
                        查询
                    </Button>
                    <Button
                        onClick={() => {
                            form.resetFields();
                            load(1);
                        }}
                    >
                        重置
                    </Button>
                </Space>
            </Form>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                loading={loading}
                scroll={{x: 1680, y: 'calc(100vh - 430px)'}}
                pagination={false}
            />
            <Pagination
                style={{marginTop: 16, textAlign: 'right'}}
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showTotal={total => `共 ${total} 条记录`}
                onChange={(current, size) => load(current, size)}
            />
            {detailRow && (
                <HistoryDetailModal
                    appId={appId}
                    historyId={detailRow.historyId}
                    taskName={detailRow.taskName || detailRow.taskId}
                    onClose={() => setDetailRow(null)}
                />
            )}
        </>
    );
}

UserLogs.propTypes = {
    appId: PropTypes.string.isRequired,
    categoryOptions: PropTypes.array.isRequired,
    taskTypeOptions: PropTypes.array.isRequired,
};

export default function PushLogPage() {
    const [{appId}] = useAppStore();
    const [authStore] = useAuthStore();
    const authorities = authStore.authorities || [];
    const isAdmin = authorities.includes('global:admin:w');
    const hasAuth = keys => isAdmin || authStore.hasAuthorities(keys, 'w');
    const canRoutine = hasAuth(['global:hr-push:w', 'app:hr-push:w']);
    const allowedSpecific = useMemo(() => getAuthorizedSpecificTypes(authStore), [authStore]);

    const categoryOptions = [
        ...(canRoutine ? [{value: 'routine', label: CATEGORY_LABELS.routine}] : []),
        ...(allowedSpecific.length ? [{value: 'specific', label: CATEGORY_LABELS.specific}] : []),
    ];
    const taskTypeOptions = Object.entries(ALL_TYPES)
        .filter(([type]) => (ROUTINE_TYPES[type] ? canRoutine : allowedSpecific.includes(type)))
        .map(([value, label]) => ({value: value.toLowerCase(), label}));

    if (!appId || categoryOptions.length === 0) {
        return null;
    }

    return (
        <AppContent>
            <Card title="推送消息日志">
                <Tabs
                    items={[
                        {
                            key: 'batch',
                            label: '按批次',
                            children: (
                                <BatchLogs
                                    appId={appId}
                                    categoryOptions={categoryOptions}
                                    taskTypeOptions={taskTypeOptions}
                                />
                            ),
                        },
                        {
                            key: 'user',
                            label: '按用户',
                            children: (
                                <UserLogs
                                    appId={appId}
                                    categoryOptions={categoryOptions}
                                    taskTypeOptions={taskTypeOptions}
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </AppContent>
    );
}
